import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Button, TextField, IconButton,
    Chip, Tooltip, InputAdornment, Checkbox, Menu, MenuItem, ListItemIcon,
    ListItemText, Select, FormControl, InputLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    FileDownload as FileDownloadIcon,
    Upload as UploadIcon,
    Category as CategoryIcon,
    Psychology as SkillIcon,
    FilterList as FilterListIcon,
    List as ListIcon
} from '@mui/icons-material';
import { skillService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import SkillFormModal from './SkillFormModal';
import CategoryFormModal from './CategoryFormModal';
import ImportModal from './ImportModal';
import BulkDeleteModal from '../User/BulkDeleteModal';
import CategoriesList from './CategoriesList';
import StatusPill from '../shared/StatusPill';
import EmptyState from '../common/EmptyState';

const Skills = () => {
    const { showToast } = useToast();
    const [skills, setSkills] = useState(null);
    const [categories, setCategories] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalSkills, setTotalSkills] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [permissions, setPermissions] = useState({
        canEdit: false,
        canDelete: false,
        canCreate: false
    });
    const [currentView, setCurrentView] = useState('skills');

    // Modal states
    const [skillFormModalOpen, setSkillFormModalOpen] = useState(false);
    const [categoryFormModalOpen, setCategoryFormModalOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [importType, setImportType] = useState(''); // 'skills' or 'categories'

    // Menu states
    const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
    const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
    const isAddMenuOpen = Boolean(addMenuAnchorEl);
    const isActionsMenuOpen = Boolean(actionsMenuAnchorEl);

    useEffect(() => {
        if (currentView === 'skills') {
            fetchSkills();
            fetchCategories();
        }
    }, [page, rowsPerPage, searchTerm, categoryFilter, currentView]);

    useEffect(() => {
        setSelectedSkills([]);
    }, [page, rowsPerPage, searchTerm, categoryFilter]);

    const fetchSkills = async () => {
        try {
            const response = await skillService.getSkills(page + 1, rowsPerPage, searchTerm, categoryFilter);
            if (response.data.success) {
                setSkills(response.data.skills);
                setTotalSkills(response.data.totalSkills);
                console.log(response.data.totalSkills, '(response.data.totalSkills');
                setPermissions(response.data.permissions);
            }
        } catch (error) {
            console.error('Error fetching skills:', error);
            showToast('Error fetching skills', 'error');
            setSkills([]);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await skillService.getCategories();
            if (response.data.success) {
                setCategories(response.data.categories);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            showToast('Error fetching categories', 'error');
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleCategoryFilterChange = (event) => {
        setCategoryFilter(event.target.value);
        setPage(0);
    };

    const handleSelectSkill = (skillId) => {
        setSelectedSkills(prev => {
            if (prev.includes(skillId)) {
                return prev.filter(id => id !== skillId);
            } else {
                return [...prev, skillId];
            }
        });
    };

    const handleSelectAllSkills = (event) => {
        if (event.target.checked && skills) {
            const currentPageSkillIds = skills.map(skill => skill.id);
            setSelectedSkills(currentPageSkillIds);
        } else {
            setSelectedSkills([]);
        }
    };

    const isAllSelected = skills && skills.length > 0 && selectedSkills.length === skills.length;

    // Menu handlers
    const handleAddMenuClick = (event) => {
        setAddMenuAnchorEl(event.currentTarget);
    };

    const handleAddMenuClose = () => {
        setAddMenuAnchorEl(null);
    };

    const handleActionsMenuClick = (event) => {
        setActionsMenuAnchorEl(event.currentTarget);
    };

    const handleActionsMenuClose = () => {
        setActionsMenuAnchorEl(null);
    };

    // Action handlers
    const handleCreateSkill = () => {
        handleAddMenuClose();
        setSelectedSkill(null);
        setSkillFormModalOpen(true);
    };

    const handleCreateCategory = () => {
        handleAddMenuClose();
        setCategoryFormModalOpen(true);
    };

    const handleEditSkill = (skill) => {
        setSelectedSkill(skill);
        setSkillFormModalOpen(true);
    };

    const handleImportCategories = () => {
        handleAddMenuClose();
        setImportType('categories');
        setImportModalOpen(true);
    };

    const handleImportSkills = () => {
        handleAddMenuClose();
        setImportType('skills');
        setImportModalOpen(true);
    };

    const handleBulkDelete = () => {
        handleActionsMenuClose();
        setBulkDeleteModalOpen(true);
    };

    const handleViewCategories = () => {
        setCurrentView('categories');
    };

    const handleBackToSkills = () => {
        setCurrentView('skills');
        // Refresh data when coming back
        fetchSkills();
        fetchCategories();
    };

    const handleSkillFormSuccess = () => {
        fetchSkills();
    };

    const handleCategoryFormSuccess = () => {
        fetchCategories();
        fetchSkills(); // Refresh skills to show updated category names
    };

    const handleImportSuccess = () => {
        if (importType === 'categories') {
            fetchCategories();
        }
        fetchSkills();
    };

    const handleExportSkills = async () => {
        try {
            const response = await skillService.exportSkills(categoryFilter, searchTerm);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'skills_export.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showToast('Skills exported successfully', 'success');
        } catch (error) {
            console.error('Error exporting skills:', error);
            showToast('Error exporting skills', 'error');
        }
    };

    const handleExportSelectedSkills = async () => {
        if (selectedSkills.length === 0) {
            showToast('Please select skills to export', 'warning');
            return;
        }

        try {
            const response = await skillService.exportSelectedSkills(selectedSkills);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'selected_skills_export.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showToast('Selected skills exported successfully', 'success');
            handleActionsMenuClose();
        } catch (error) {
            console.error('Error exporting selected skills:', error);
            showToast('Error exporting selected skills', 'error');
        }
    };

    if (currentView === 'categories') {
        return <CategoriesList onBack={handleBackToSkills} />;
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom className='page-title'>
                Skills Management
            </Typography>
            <Typography variant="body2" sx={{ mb: 4 }}>
                Create categories first, then add skills to those categories.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Skills, Categories"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={categoryFilter}
                            onChange={handleCategoryFilterChange}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Category filter' }}
                            startAdornment={
                                <InputAdornment position="end">
                                    <FilterListIcon fontSize="small" />
                                </InputAdornment>
                            }
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {category.category_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedSkills.length > 0 && (
                        <Button
                            variant="outlined"
                            onClick={handleActionsMenuClick}
                            aria-controls={isActionsMenuOpen ? 'actions-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={isActionsMenuOpen ? 'true' : undefined}
                            sx={{ mr: 1 }}
                        >
                            Actions
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<ListIcon />}
                        onClick={handleViewCategories}
                    >
                        Categories
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportSkills}
                    >
                        Export All
                    </Button>

                    {permissions.canCreate && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddMenuClick}
                            aria-controls={isAddMenuOpen ? 'add-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={isAddMenuOpen ? 'true' : undefined}
                        >
                            Add
                        </Button>
                    )}
                </Box>

                <Menu
                    id="add-menu"
                    anchorEl={addMenuAnchorEl}
                    open={isAddMenuOpen}
                    onClose={handleAddMenuClose}
                    MenuListProps={{
                        'aria-labelledby': 'add-button',
                    }}
                >
                    <MenuItem onClick={handleCreateSkill}>
                        <ListItemIcon>
                            <SkillIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Add Skill</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleCreateCategory}>
                        <ListItemIcon>
                            <CategoryIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Add Category</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleImportCategories}>
                        <ListItemIcon>
                            <UploadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Import Categories</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleImportSkills}>
                        <ListItemIcon>
                            <UploadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Import Skills</ListItemText>
                    </MenuItem>
                </Menu>
                <Menu
                    id="actions-menu"
                    anchorEl={actionsMenuAnchorEl}
                    open={isActionsMenuOpen}
                    onClose={handleActionsMenuClose}
                    MenuListProps={{
                        'aria-labelledby': 'actions-button',
                    }}
                >
                    <MenuItem onClick={handleExportSelectedSkills}>
                        <ListItemIcon>
                            <FileDownloadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Export Selected</ListItemText>
                    </MenuItem>
                    {permissions.canDelete && (
                        <MenuItem onClick={handleBulkDelete}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Delete Skills</ListItemText>
                        </MenuItem>
                    )}
                </Menu>
            </Box>

            {selectedSkills.length > 0 && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="white">
                        {selectedSkills.length} skill{selectedSkills.length > 1 ? 's' : ''} selected
                    </Typography>
                </Box>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ width: '40px', pl: 0.5 }}>
                                <Checkbox
                                    indeterminate={selectedSkills.length > 0 && !isAllSelected}
                                    checked={isAllSelected}
                                    onChange={handleSelectAllSkills}
                                    className='table-checkboxes'
                                    size='small'
                                />
                            </TableCell>
                            <TableCell>Skill Name</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {skills === null ? (
                            null // Don't show anything while loading
                        ) : skills.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14): a plain line. */}
                                    <EmptyState framed={false} title="No skills found" message={null} />
                                </TableCell>
                            </TableRow>
                        ) : (
                            skills.map((skill) => (
                                <TableRow key={skill.id}>
                                    <TableCell padding="checkbox" sx={{ width: '40px', pl: 0.5 }}>
                                        <Checkbox
                                            checked={selectedSkills.includes(skill.id)}
                                            onChange={() => handleSelectSkill(skill.id)}
                                            className='table-checkboxes'
                                            size='small'
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {/* <Typography variant="body2" fontWeight="medium">
                                            {skill.skill_name}
                                        </Typography> */}
                                        {permissions.canEdit ? (
                                            <Box
                                                sx={{
                                                    cursor: "pointer",
                                                    color: "primary.main",
                                                }}
                                                onClick={() => handleEditSkill(skill)}
                                            >
                                                {skill.skill_name}
                                            </Box>
                                        ) : (
                                            <Box>{skill.skill_name}</Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {skill.category_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {skill.description || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>                                        
                                        <StatusPill status={skill.status ? 'Active' : 'Inactive'}/>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            {permissions.canEdit && (
                                                <Tooltip title="Edit Skill">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditSkill(skill)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalSkills}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* Modals */}
            <SkillFormModal
                open={skillFormModalOpen}
                onClose={() => setSkillFormModalOpen(false)}
                skill={selectedSkill}
                categories={categories}
                onSuccess={handleSkillFormSuccess}
            />

            <CategoryFormModal
                open={categoryFormModalOpen}
                onClose={() => setCategoryFormModalOpen(false)}
                onSuccess={handleCategoryFormSuccess}
            />

            <ImportModal
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                importType={importType}
                onSuccess={handleImportSuccess}
            />

            <BulkDeleteModal
                open={bulkDeleteModalOpen}
                onClose={() => setBulkDeleteModalOpen(false)}
                ids={selectedSkills}
                onSuccess={() => {
                    showToast('Skills deleted successfully', 'success');
                    setBulkDeleteModalOpen(false);
                    fetchSkills();
                    setSelectedSkills([]);
                }}
                deleteFunction={skillService.bulkDeleteSkills}
                title="Delete Skills"
                itemName="skill"
            />
        </Box>
    );
};

export default Skills;